import React, { useState, useMemo, useCallback } from "react";
import Table from "../../../shared/components/common/Table";
import SearchBar from "../../../shared/components/common/SearchBar";
import TableToolbar from "../../../shared/components/common/TableToolbar";
import { getCountryFromNumber } from "../constants/data";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { isViewer } from "../../../shared/utils/roles";

const PurchasedNumbers = ({
  numbers,
  onUploadGreeting,
  onPlayGreeting,
  onRelease,
  spreadTable,
  onSpreadTableToggle,
  loading = false,
  pageSize = 10,
  canEdit = true,
}) => {
  const currentUser = useSelector(selectCurrentUser);
  const viewer = isViewer(currentUser?.user_role);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [phoneFilter, setPhoneFilter] = useState("");
  const [nicknameFilter, setNicknameFilter] = useState("");
  const [greetingFilter, setGreetingFilter] = useState("all");

  const greetingOptions = [
    { value: "all", name: "All" },
    { value: "has", name: "Has Greeting" },
    { value: "none", name: "No Greeting" },
  ];
// start from signup
  const filteredNumbers = useMemo(() => {
    let result = numbers || [];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (n) =>
          n.phoneNumber.toLowerCase().includes(lower) ||
          (n.nickname && n.nickname.toLowerCase().includes(lower))
      );
    }
    if (phoneFilter) {
      const lower = phoneFilter.toLowerCase();
      result = result.filter((n) => n.phoneNumber.toLowerCase().includes(lower));
    }
    if (nicknameFilter) {
      const lower = nicknameFilter.toLowerCase();
      result = result.filter((n) => n.nickname && n.nickname.toLowerCase().includes(lower));
    }
    if (greetingFilter === "has") {
      result = result.filter((n) => n.greetingFile && n.greetingFile !== "-");
    } else if (greetingFilter === "none") {
      result = result.filter((n) => !n.greetingFile || n.greetingFile === "-");
    }
    return result;
  }, [numbers, searchTerm, phoneFilter, nicknameFilter, greetingFilter]);

  const hasAnyNumbers = (numbers || []).length > 0;

  const filterFields = useMemo(() => [
    {
      key: "phoneNumber",
      label: "Phone Number",
      type: "text",
      value: phoneFilter,
      onChange: setPhoneFilter,
      placeholder: "Filter by phone number",
    },
    {
      key: "nickname",
      label: "Nickname",
      type: "text",
      value: nicknameFilter,
      onChange: setNicknameFilter,
      placeholder: "Filter by nickname",
    },
    {
      key: "greetingFile",
      label: "Greeting File",
      type: "select",
      value: greetingFilter,
      onChange: setGreetingFilter,
      options: greetingOptions,
    },
  ], [phoneFilter, nicknameFilter, greetingFilter]);

  const columns = useMemo(() => {
    const cols = [
      {
        Header: () => (
          <input
            type="checkbox"
            className="w-4 h-4 text-primary border-gray-300 rounded-sm focus:ring-primary"
            disabled
            style={{
              display: "none",
            }}
          />
        ),
        accessor: "checkbox",
        disableSortBy: true,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            className="w-4 h-4 text-primary border-gray-300 rounded-sm focus:ring-primary"
          />
        ),
      },
      {
        Header: "PHONE NUMBER",
        accessor: "phoneNumber",
        Cell: ({ value }) => (
          <div className="flex flex-col">
            <span className="text-content text-sm font-medium">{value}</span>
            <span className="text-gray-500 text-xs">{getCountryFromNumber(value)}</span>
          </div>
        ),
      },
      {
        Header: "NICKNAME",
        accessor: "nickname",
        Cell: ({ value }) => <span className="text-content text-xs sm:text-sm">{value || "-"}</span>,
      },
      {
        Header: "GREETING FILE",
        accessor: "greetingFile",
        Cell: ({ row, value }) => (
          <span className="text-content text-xs sm:text-sm">
            {(value && value !== "-") || row.original.greetingUrl ? (
              <button
                type="button"
                onClick={() => onPlayGreeting?.(row.original)}
                className="text-primary underline underline-offset-2 hover:text-primary/80"
              >
                {value && value !== "-" ? value : "Greeting available"}
              </button>
            ) : (
              "-"
            )}
          </span>
        ),
      },
    ];

    if (!viewer) {
      cols.push({
        Header: "ACTION",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onUploadGreeting(row.original)}
              disabled={!canEdit}
              className="px-4 py-1.5 bg-primary text-white rounded-md text-xs font-bold tracking-wider hover:bg-success/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Upload
            </button>
            <button
              onClick={() => onRelease(row.original)}
              disabled={!canEdit || !row.original.phoneNumber || row.original.phoneNumber === "-"}
              className="px-4 py-1.5 border border-primary text-primary bg-white rounded-md text-xs font-bold tracking-wider hover:bg-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete
            </button>
          </div>
        ),
      })
    }

    return cols;
  }, [onUploadGreeting, onPlayGreeting, onRelease, viewer, canEdit]);

  return (
    <div className="mb-6">
      <h2 className="text-base font-bold text-primary sm:mx-2 mb-3">
        Purchased Numbers
      </h2>
      <div
        className={`sm:mx-2 border border-line rounded-t-xl shadow-sm flex flex-col logs-table-area ${
          spreadTable ? " table-zoom-out" : ""
        }`}
      >
        <div className="p-4 bg-white border-b border-line flex max-[400px]:flex-col flex-row justify-between sm:items-center rounded-t-xl max-[400px]:gap-4 gap-2 sm:gap-0">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search number"
            useMUIicons={false}
          />
          <TableToolbar
            spreadTable={spreadTable}
            onSpreadTableToggle={onSpreadTableToggle}
            displayData={filteredNumbers}
            columns={columns}
            filterFields={filterFields}
          />
        </div>

        {!loading && filteredNumbers.length === 0 ? (
          <div className="w-full px-6 py-10 bg-white rounded-b-xl text-center">
            <p className="text-sm font-semibold text-primary mb-1">
              No purchased numbers available
            </p>
            <p className="text-xs text-gray-500">
              {hasAnyNumbers
                ? "No purchased number found."
                : "No purchased number found. Please purchase a number from the available numbers first."}
            </p>
          </div>
        ) : (
        <div className="flex-1 overflow-visible">
          <Table
            columns={columns}
            data={filteredNumbers}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            rowIdKey="id"
            loading={loading}
          />
        </div>
        )}
      </div>
    </div>
  );
};

export default PurchasedNumbers;
